import React, { useState, useEffect } from 'react';
import { INACTIVITY_TIMEOUT_MS, COUNTDOWN_SECONDS } from './constants';

interface Props {
  lastSpeechTimestamp: React.MutableRefObject<number>;
  onTimeout: () => void;
  enabled: boolean;
}

export const InactivityGuard: React.FC<Props> = ({ lastSpeechTimestamp, onTimeout, enabled }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!enabled) { setShowPrompt(false); return; }

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastSpeechTimestamp.current;
      if (elapsed > INACTIVITY_TIMEOUT_MS && !showPrompt) {
        setShowPrompt(true);
        setCountdown(COUNTDOWN_SECONDS);
      } else if (elapsed <= INACTIVITY_TIMEOUT_MS && showPrompt) {
        setShowPrompt(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [enabled, showPrompt, lastSpeechTimestamp]);

  useEffect(() => {
    if (!showPrompt) return;
    const timer = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [showPrompt]);

  // Fire onTimeout when countdown reaches 0 (avoids side-effect inside state setter)
  useEffect(() => {
    if (showPrompt && countdown === 0) {
      onTimeout();
    }
  }, [showPrompt, countdown, onTimeout]);

  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 text-center border border-[#D4D4D4]">
        <p className="text-2xl text-[#1A1A1A] mb-2">Still there?</p>
        <p className="text-[#888888] text-lg">Session will end in {countdown}s</p>
        <button
          onClick={() => { lastSpeechTimestamp.current = Date.now(); setShowPrompt(false); }}
          className="mt-4 px-6 py-2 bg-[#30D158]/20 text-[#30D158] rounded-xl border border-[#30D158]/30"
        >
          I'm here!
        </button>
      </div>
    </div>
  );
};
